<script lang="ts" setup>
import { ref } from 'vue';
import type { USState, ValidationRules } from '@Shared';
import { US_STATES, useFormValidation, useFieldStatus } from '@Shared';
import type { Address } from '@Types/WiringInstrucions.interface';

const props = defineProps<{
    address: Address;
}>();

const findState = (value: string | null | undefined): USState | null => {
    const written = value?.trim().toLowerCase();
    if (!written) return null;
    return US_STATES.find(
        s => s.abbreviation.toLowerCase() === written || s.name.toLowerCase() === written
    ) ?? null;
};

const initialState = findState(props.address?.state);

const form = ref<Address>({
    address1: props.address?.address1 ?? '',
    address2: props.address?.address2 ?? '',
    city: props.address?.city ?? '',
    state: initialState?.abbreviation ?? '',
    postalCode: props.address?.postalCode ?? '',
});

const validationRules = ref<ValidationRules>({
    'city': [{ required: true, message: 'City is required' }],
    'postalCode': [
        { validateZipCode: true, message: 'Zip code must be 5 digits' },
    ],
});

const formValidation = useFormValidation(form, validationRules);
const { turnOnValidation, hasError } = formValidation;
const { getFieldStatus, getFieldMessage } = useFieldStatus(formValidation);

const selectedState = ref<USState | null>(initialState);

const onStateSelect = (state: USState) => {
    selectedState.value = state;
    form.value.state = state.abbreviation;
};

const onNativeStateAutofill = (event: Event) => {
    const match = findState((event.target as HTMLSelectElement).value);
    if (match && match.abbreviation !== selectedState.value?.abbreviation) onStateSelect(match);
};

const validate = async (): Promise<boolean> => {
    await turnOnValidation(true);
    return !hasError.value;
};

/** The live form value. Read by the parent on save, so nothing depends on an
 * intermediate copy staying in sync. */
const getAddress = (): Address => ({ ...form.value });

defineExpose({ validate, getAddress });
</script>

<template>
    <app-form-content-layout class="confirm-address-edit">
        <app-text-input
            v-model="form.address1"
            label="Address line"
            :field-status="getFieldStatus('address1')"
            :message="getFieldMessage('address1')"
            data-id="confirm-address1"
            data-validation-field="address1"
        />

        <app-text-input
            v-model="form.address2"
            label="Apartment, suite, etc."
            data-id="confirm-address2"
        />

        <app-text-input
            v-model="form.city"
            label="City"
            is-required
            :field-status="getFieldStatus('city')"
            :message="getFieldMessage('city')"
            data-id="confirm-city"
            data-validation-field="city"
        />

        <div class="state-field">
            <app-dropdown
                :model-value="selectedState"
                @update:model-value="onStateSelect"
                :options="US_STATES"
                id-field="abbreviation"
                display-field="name"
                label="State"
                :field-status="getFieldStatus('state')"
                :message="getFieldMessage('state')"
                data-id="confirm-state"
                data-validation-field="state"
            />
            <select
                name="state"
                autocomplete="address-level1"
                aria-hidden="true"
                tabindex="-1"
                class="state-autofill-mirror"
                :value="selectedState?.abbreviation ?? ''"
                @change="onNativeStateAutofill"
                @input="onNativeStateAutofill">
                <option value=""></option>
                <option
                    v-for="s in US_STATES"
                    :key="s.abbreviation"
                    :value="s.abbreviation">
                    {{ s.name }}
                </option>
            </select>
        </div>

        <app-masked-input
            v-model="form.postalCode"
            label="Zip code"
            mask="#####"
            input-mode="numeric"
            :field-status="getFieldStatus('postalCode')"
            :message="getFieldMessage('postalCode')"
            data-id="confirm-zip-code"
            data-validation-field="postalCode"
        />
    </app-form-content-layout>
</template>

<style lang="scss" scoped>
@use "certifid-v2-client-hub-styles" as *;

.state-field {
    position: relative;
}

.state-autofill-mirror {
    position: absolute;
    left: -10000px;
    width: 1px;
    height: 1px;
    overflow: hidden;
}
</style>
